import { Line } from '@react-three/drei'
import * as THREE from 'three'
import { useMemo } from 'react'

// 곡선: 최근(끝)으로 갈수록 가팔라지게
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

type Props = {
  points: THREE.Vector3[]
  /** 두 옵션 중 하나만 쓰면 됩니다 */
  baseColor?: THREE.ColorRepresentation  // ex) 0x1477D2
  baseHue?: number                       // ex) 210
  satOld?: number        // 오래된 구간 채도(%)
  satRecent?: number     // 최근 구간 채도(%)
  lightOld?: number      // 오래된 구간 밝기(%)
  lightRecent?: number   // 최근 구간 밝기(%)
  recentSpanRatio?: number // 최근(진한) 구간 비율 0~1 (작을수록 ‘진한 영역’이 짧아짐)
  lineWidth?: number
  opacity?: number
  frustumCulled?: boolean
  invertOrder?: boolean  // true면 points가 최근→과거 순으로 들어왔을 때 사용
}

export default function PastGradientLine({
  points,
  baseColor,
  baseHue,
  satOld = 60,
  satRecent = 100,
  lightOld = 12,
  lightRecent = 72,
  recentSpanRatio = 0.3,
  lineWidth = 4,
  opacity = 0.95,
  frustumCulled = false,
  invertOrder = false,
}: Props) {
  const vertexColors = useMemo(() => {
    const n = Math.max(points.length, 2)

    // 1) hue 결정: baseHue 우선, 없으면 baseColor에서 추출
    let hue = typeof baseHue === 'number' ? baseHue : 210
    if (baseHue == null && baseColor != null) {
      const c = new THREE.Color(baseColor as any)
      const hsl = { h: 0, s: 0, l: 0 }
      c.getHSL(hsl)
      hue = Math.round(hsl.h * 360)
    }

    // 2) 버텍스별 색상 생성
    const colors = new Array<THREE.Color>(n)
    // recentSpanRatio 범위 계산
    const clamp01 = (x: number) => Math.min(1, Math.max(0, x))
    const span = clamp01(recentSpanRatio <= 0 ? 0 : recentSpanRatio)
    const startRecent = 1 - (span > 0 ? span : 1)

    for (let i = 0; i < n; i++) {
      // i=0: 가장 오래된 점, i=n-1: 가장 최근 점  (기본 가정)
      // 만약 points가 최근→과거 순이라면 invertOrder=true로 뒤집어 처리
      const idx = invertOrder ? n - 1 - i : i
      const tBasic = idx / (n - 1) // 0..1 (과거→최근)

      // ‘최근 구간만 더 강하게’ 강조: recentSpanRatio 적용
      const norm = span <= 0 ? tBasic : (tBasic - startRecent) / (span || 1e-6)
      const clamped = clamp01(norm)
      const k = easeOutCubic(clamped) // 최근쪽에서 가팔라지게

      const S = satOld + (satRecent - satOld) * k
      const L = lightOld + (lightRecent - lightOld) * k
      colors[i] = new THREE.Color(`hsl(${hue}, ${S}%, ${L}%)`)
    }
    return colors
  }, [
    points,
    baseHue,
    baseColor,
    satOld,
    satRecent,
    lightOld,
    lightRecent,
    recentSpanRatio,
    invertOrder,
  ])

  return (
    <Line
      points={points}
      vertexColors={vertexColors}
      lineWidth={lineWidth}
      transparent
      opacity={opacity}
      frustumCulled={frustumCulled}
    />
  )
}
